"use client";

import { JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { scrollIntoViewX } from "@/lib/scroll-into-view-x";
import { emitModuleInteraction } from "@/lib/module-interaction";
import { ScatterChart } from "@/components/charts/scatter-chart";
import { Scatter } from "@/components/charts/scatter";
import {
  QuadrantTiles,
  type QuadrantTile,
} from "@/components/charts/quadrant-tiles";
import { ChartTooltip } from "@/components/charts/tooltip";
import { TooltipContent } from "@/components/charts/tooltip/tooltip-content";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import {
  ChartLegendHoverProvider,
  type ChartSelectSource,
} from "@/components/charts/chart-legend-hover";
import {
  type LegendSelectSource,
  useLegendClickSync,
} from "@/components/charts/use-legend-click-sync";
import {
  type ChartView,
  ChartViewToggle,
  chartViewToMode,
} from "@/components/charts/projection/chart-view-toggle";
import { MAX_PROJECTED_POINTS } from "@/components/charts/projection/iso-projection";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { CHART_PLOT_HEIGHT, CHART_PLOT_WIDTH } from "@/config/layout";
import { importsTokenScope } from "./imports-token-scope";
import {
  QUADRANT_META,
  quadrantLabel,
  QUADRANT_ORDER,
  type QuadrantKey,
  quadrantOf,
} from "./quadrant-meta";

export type QuadrantMatrixItem = {
  id: string;
  name: string;
  effort: number;
  impact: number;
};

export type QuadrantMatrixData = {
  items: QuadrantMatrixItem[];
  xLabel?: string;
  yLabel?: string;
};

const DEFAULT_DATA: QuadrantMatrixData = {
  xLabel: "Effort",
  yLabel: "Impact",
  items: [
    { id: "1", name: "Fix login bug", effort: 15, impact: 30 },
    { id: "2", name: "Add SSO", effort: 42, impact: 82 },
    { id: "3", name: "Rewrite billing", effort: 85, impact: 88 },
    { id: "4", name: "Dark mode", effort: 22, impact: 48 },
    { id: "5", name: "Onboarding v2", effort: 62, impact: 78 },
    { id: "6", name: "Legacy migration", effort: 90, impact: 32 },
    { id: "7", name: "Feature toggles", effort: 32, impact: 65 },
    { id: "8", name: "Search redesign", effort: 65, impact: 42 },
    { id: "9", name: "Analytics events", effort: 18, impact: 68 },
    { id: "10", name: "Copy audit", effort: 8, impact: 20 },
    { id: "11", name: "Rate limiting", effort: 35, impact: 52 },
    { id: "12", name: "GraphQL layer", effort: 75, impact: 25 },
    { id: "13", name: "PDF export", effort: 48, impact: 40 },
    { id: "14", name: "Email digest", effort: 25, impact: 35 },
  ],
};

const DOT_RADIUS = 4;
const X_RANGE_PAD = DOT_RADIUS + 10;
const MARGIN = {
  top: 40,
  right: 40 - X_RANGE_PAD,
  bottom: 60,
  left: 60 - X_RANGE_PAD,
};
const X_DOMAIN: [number, number] = [0, 100];
const DIVIDERS = [50];
const GUIDES = [25, 75];
const END_LABELS: [string, string] = ["low", "high"];
const seriesKey = (q: QuadrantKey) => `q_${q}`;

export default function ModuleQuadrantMatrix({
  view3d,
  data = DEFAULT_DATA,
  showLegend = true,
  grayscale,
  accent,
  className,
}: {
  view3d?: boolean;
  data?: QuadrantMatrixData;
  showLegend?: boolean;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const items = useMemo(() => data.items ?? [], [data.items]);
  const xLabel = stripInlineMarkdown(data.xLabel) || t("Effort");
  const yLabel = stripInlineMarkdown(data.yLabel) || t("Impact");

  const quadrants = useMemo(
    () => items.map((it) => quadrantOf(it.effort, it.impact)),
    [items],
  );
  const quadrantCounts = useMemo(() => {
    const out: Record<QuadrantKey, number> = {
      "quick-wins": 0,
      "big-bets": 0,
      "fill-ins": 0,
      "time-sinks": 0,
    };
    quadrants.forEach((q) => {
      out[q]++;
    });
    return out;
  }, [quadrants]);

  const activeQuadrants = useMemo(
    () => QUADRANT_ORDER.filter((k) => quadrantCounts[k] > 0),
    [quadrantCounts],
  );
  const hasTop =
    quadrantCounts["quick-wins"] > 0 || quadrantCounts["big-bets"] > 0;
  const hasBottom =
    quadrantCounts["fill-ins"] > 0 || quadrantCounts["time-sinks"] > 0;
  const collapseY = hasTop !== hasBottom;
  const yMin = collapseY && hasTop ? 50 : 0;
  const yMax = collapseY && hasBottom ? 50 : 100;
  const yDomain = useMemo<[number, number]>(() => [yMin, yMax], [yMin, yMax]);
  const chartHeight = collapseY
    ? CHART_PLOT_HEIGHT.quadrantCollapsed
    : CHART_PLOT_HEIGHT.quadrant;

  const chartData = useMemo(
    () =>
      items.map((it, i) => ({
        id: it.id,
        x: it.effort,
        [seriesKey(quadrants[i])]: it.impact,
      })),
    [items, quadrants],
  );

  const legendItems = activeQuadrants.map((k) => ({
    id: k,
    label: quadrantLabel(k, t),
    color: QUADRANT_META[k].color,
    value: quadrantCounts[k],
  }));

  const canProject = Boolean(view3d) && items.length <= MAX_PROJECTED_POINTS;
  const [view, setView] = useState<ChartView>("3d");
  const projection = canProject ? chartViewToMode(view) : "flat";

  const [legendHovered, setLegendHovered] = useState<number | null>(null);
  const {
    activeIndex,
    handleSelect,
    setLabelRef,
    selectedOrigin,
    legendSelectedIndex,
    filterIndex,
  } = useLegendClickSync();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedIdRef = useRef<string | null>(null);

  const selectedItemIndex =
    selectedId !== null ? items.findIndex((it) => it.id === selectedId) : -1;
  const selectedRow = selectedItemIndex >= 0 ? selectedItemIndex : null;
  const selectedSeries =
    selectedRow !== null ? activeQuadrants.indexOf(quadrants[selectedRow]) : -1;
  const elementIndex = selectedSeries >= 0 ? selectedSeries : null;

  const togglePoint = useCallback(
    (_series: number, source?: ChartSelectSource) => {
      const id = source?.id != null ? String(source.id) : null;
      if (id === null) return;
      const index = items.findIndex((it) => it.id === id);
      if (index < 0) return;
      const next = selectedIdRef.current === id ? null : id;
      selectedIdRef.current = next;
      setSelectedId(next);
      emitModuleInteraction(
        source?.element,
        next === null ? "deselect" : "select",
        { kind: "point", id, index },
      );
    },
    [items],
  );

  const selectLegend = useCallback(
    (i: number, source?: LegendSelectSource) => {
      selectedIdRef.current = null;
      setSelectedId(null);
      handleSelect(i, source);
    },
    [handleSelect],
  );

  const legendFilter = legendHovered ?? filterIndex;
  const legendFilterKey =
    legendFilter !== null ? activeQuadrants[legendFilter] ?? null : null;

  const tiles = useMemo<QuadrantTile[]>(
    () =>
      activeQuadrants.map((k) => {
        const meta = QUADRANT_META[k];
        return {
          key: k,
          label: quadrantLabel(k, t),
          color: meta.color,
          tint: meta.tint,
          x: meta.half.x === "left" ? [0, 50] : [50, 100],
          y: meta.half.y === "top" ? [50, 100] : [0, 50],
          labelX: meta.half.x === "left" ? "start" : "end",
          labelY: meta.half.y === "top" ? "top" : "bottom",
          dimmed: legendFilterKey !== null && legendFilterKey !== k,
        };
      }),
    [activeQuadrants, legendFilterKey, t],
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollQuadrant =
    legendSelectedIndex !== null
      ? activeQuadrants[legendSelectedIndex] ?? null
      : null;
  const scrollRow =
    selectedRow ??
    (scrollQuadrant ? quadrants.indexOf(scrollQuadrant) : -1);
  const scrollAlign = selectedRow !== null ? "nearest" : "center";
  useEffect(() => {
    if (scrollRow < 0) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    scrollIntoViewX(
      container,
      container.querySelector(`[data-point-index="${scrollRow}"]`),
      { align: scrollAlign, once: false },
    );
  }, [scrollRow, scrollAlign]);

  const pointId = useCallback(
    (datum: Record<string, unknown>) => String(datum.id),
    [],
  );

  return (
    <ChartLegendHoverProvider
      hoveredIndex={legendHovered}
      onHoverChange={setLegendHovered}
      selectedIndex={activeIndex}
      selectedOrigin={selectedOrigin}
      elementIndex={elementIndex}
      onElementToggle={togglePoint}
    >
      <div
        className={cn("flex w-full min-w-0 flex-col gap-4", className)}
        style={importsTokenScope(grayscale, accent)}
      >
        {canProject ? (
          <div className="flex h-8 items-center justify-end">
            <ChartViewToggle
              className="shrink-0"
              kind="quadrant"
              onChange={setView}
              value={view}
            />
          </div>
        ) : null}

        <SwipeScroll ref={scrollContainerRef} className="relative w-full">
          <div
            className="w-full"
            style={{ minWidth: CHART_PLOT_WIDTH.quadrant, height: chartHeight }}
          >
            <ScatterChart
              aspectRatio="auto"
              className="h-full"
              data={chartData}
              interaction="point"
              margin={MARGIN}
              projection={projection}
              projectionLayout="floor"
              xDataKey="x"
              xDomain={X_DOMAIN}
              xScaleKind="linear"
              yDomain={yDomain}
            >
              <QuadrantTiles
                tiles={tiles}
                xDividers={DIVIDERS}
                xEndLabels={END_LABELS}
                xGuides={GUIDES}
                xTitle={xLabel}
                yDividers={DIVIDERS}
                yGuides={GUIDES}
                yTitle={yLabel}
              />
              {activeQuadrants.map((k) => (
                <Scatter
                  dataKey={seriesKey(k)}
                  fill={QUADRANT_META[k].color}
                  inactiveBlur={0}
                  inactiveOpacity={0.25}
                  key={k}
                  pointId={pointId}
                  radius={DOT_RADIUS}
                  ringGap={0}
                  selectedIndex={selectedRow}
                  stroke="var(--background)"
                  strokeWidth={1.5}
                />
              ))}
              <ChartTooltip
                anchor="element"
                content={({ index }) => {
                  const item = items[index];
                  if (!item) return null;
                  const meta = QUADRANT_META[quadrants[index]];
                  return (
                    <TooltipContent
                      rows={[
                        { color: meta.color, label: quadrantLabel(quadrants[index], t), value: "" },
                        {
                          color: "var(--chart-2)",
                          label: xLabel,
                          value: item.effort,
                        },
                        {
                          color: "var(--chart-2)",
                          label: yLabel,
                          value: item.impact,
                        },
                      ]}
                      title={stripInlineMarkdown(item.name) || undefined}
                    />
                  );
                }}
                showCrosshair={false}
                showDatePill={false}
                showDots={false}
              />
            </ScatterChart>
          </div>
        </SwipeScroll>

        {showLegend && (
          <SwipeScroll>
            <Legend
              items={legendItems}
              hoveredIndex={legendHovered}
              onHoverChange={setLegendHovered}
              selectedIndex={legendSelectedIndex}
              onSelect={selectLegend}
              onLabelRef={setLabelRef}
              className="flex flex-row gap-1"
            >
              <LegendItem className="flex shrink-0 items-center gap-2 px-2 py-1">
                <LegendMarker />
                <LegendLabel />
                <LegendValue />
              </LegendItem>
            </Legend>
          </SwipeScroll>
        )}
      </div>
    </ChartLegendHoverProvider>
  );
}
